import { useEffect, useRef, useState } from "react";

/** ---------------------- Tipos da API ---------------------- */
export type SaidaItemAPI = {
  id: number;
  movimentacao_id: number;
  produto_id: number;
  quantidade: number;
  custo_unitario: string | null;
  produto: {
    id: number;
    codigo: string;
    nome: string;
    posicao: string | null;
  };
};

// 1) Tipo da movimentação de venda ----------------------------
export type SaidaMovAPI = {
  movimentacao: {
    id: number;
    tipo?: "servico" | "venda" | "outro";
    tipo_full?: string | null;
    responsavel_id: number | null;
    responsavel_nome: string | null; // 👈 vamos usar como "Responsável"
    departamento_origem: string | null;
    os_numero?: string | null;
    proposta_venda?: string | null;
    empresa_cliente?: string | null;
    cliente_nome?: string | null; // 👈 cliente pessoa
    criado_em: string;
  };
  itens: SaidaItemAPI[];
};

type RespAPI = {
  ok: boolean;
  meta: {
    filtros: {
      date_from: string | null;
      date_to: string | null;
      order: "ASC" | "DESC";
      limit: number;
      offset: number;
    };
    count: number;
  };
  data: SaidaMovAPI[];
};

/** ---------------------- Constantes ---------------------- */
const BASE = "https://api.eva-googlemarine.com/departamentos/suprimentos";
const URL = `${BASE}/vendas`;
const URL_AWAIT = `${BASE}/saidas/await`; // reaproveitando o mesmo /await das saídas

const POLL_FALLBACK_MS = 15000;
const ERROR_BACKOFF_MS = 5000;
const AWAIT_TIMEOUT_MS = 3000;
const AWAIT_GAP_MS = 120;

/** ---------------------- Utils ---------------------- */
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const jitter = (ms: number) => ms + Math.floor(Math.random() * 400);
const isNA = (s?: string | null) =>
  !s || s.trim().length === 0 || s.trim().toLowerCase() === "n/a";

function toBRT(iso: string): { data: string; hora: string } {
  if (!iso) return { data: "-", hora: "-" };
  const m = iso.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?Z$/
  );
  if (!m) return { data: iso, hora: "" };
  const [, ano, mes, dia, hora, minuto] = m;
  return { data: `${dia}/${mes}/${ano}`, hora: `${hora}:${minuto}` };
}

function formatBRL(x: number): string {
  return x.toLocaleString("pt-BR", { minimumFractionDigits: 2 });
}

/** ---------------------- Tabela ---------------------- */
/**
 * Ordem precisa bater com os headers do componente:
 * [
 *   "PV",
 *   "Tipo",
 *   "Código",
 *   "Nome do Produto",
 *   "Quantidade",
 *   "Custo Unitário",
 *   "Cliente",
 *   "Responsável",
 *   "Data",
 *   "Hora",
 * ]
 */
export type SaidaRowUI = [
  string, // PV
  string, // Tipo
  string, // Código
  string, // Nome do Produto
  string, // Quantidade
  string, // Custo Unitário
  string, // Cliente
  string, // Responsável
  string, // Data
  string // Hora
];

// 2) Flatten (onde monta as linhas) ----------------------------
function flattenToRows(movs: SaidaMovAPI[]): SaidaRowUI[] {
  const rows: SaidaRowUI[] = [];

  for (const m of movs) {
    const isVenda =
      m.movimentacao.tipo_full === "venda-baixa" ||
      m.movimentacao.tipo === "venda";
    if (!isVenda) continue;

    const { data, hora } = toBRT(m.movimentacao.criado_em);

    // PV / Proposta / OS
    const proposta = m.movimentacao.proposta_venda?.trim() ?? "";
    const os = m.movimentacao.os_numero?.trim() ?? "";
    const pv = !isNA(proposta) ? proposta : os || "-";

    const tipo = m.movimentacao.tipo ?? "-";

    // Cliente: tenta cliente_nome → empresa_cliente → "-"
    const clienteNome = m.movimentacao.cliente_nome?.trim() ?? "";
    const empresaCliente = m.movimentacao.empresa_cliente?.trim() ?? "";
    const cliente = !isNA(clienteNome)
      ? clienteNome
      : !isNA(empresaCliente)
      ? empresaCliente
      : "-";

    // Responsável: vem de responsavel_nome
    const responsavel = m.movimentacao.responsavel_nome?.trim() || "-";

    for (const it of m.itens) {
      const qtd = Number(it.quantidade || 0);
      const custo = Number(it.custo_unitario ?? "0");

      rows.push([
        pv, // PV
        tipo, // Tipo
        it.produto.codigo, // Código
        it.produto.nome, // Nome do Produto
        String(qtd), // Quantidade
        `R$ ${formatBRL(custo)}`, // Custo Unitário
        cliente, // Cliente
        responsavel, // Responsável 👈
        data, // Data
        hora, // Hora
      ]);
    }
  }

  return rows;
}

/** ---------------------- Fetchers ---------------------- */
export type VendasQuery = Partial<{
  date_from: string; // YYYY-MM-DD
  date_to: string; // YYYY-MM-DD
  order: "asc" | "desc";
  limit: number;
  offset: number;
}>;

function buildQuery(q?: VendasQuery): string {
  if (!q) return "";
  const p = new URLSearchParams();
  if (q.date_from) p.set("date_from", q.date_from);
  if (q.date_to) p.set("date_to", q.date_to);
  if (q.order) p.set("order", q.order);
  if (typeof q.limit === "number") p.set("limit", String(q.limit));
  if (typeof q.offset === "number") p.set("offset", String(q.offset));
  const s = p.toString();
  return s ? `?${s}` : "";
}

export async function fetchVendasOnce(
  query?: VendasQuery
): Promise<{ rows: SaidaRowUI[]; meta: RespAPI["meta"] | null }> {
  const res = await fetch(`${URL}${buildQuery(query)}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = (await res.json()) as RespAPI;
  if (!json.ok) return { rows: [], meta: json.meta ?? null };
  return { rows: flattenToRows(json.data), meta: json.meta ?? null };
}

/** ---------------------- Await (long-poll) ---------------------- */
type AwaitResult = { changed: boolean; versao: number | null };

async function awaitChange(
  since: number,
  signal?: AbortSignal
): Promise<AwaitResult | null> {
  try {
    const url = `${URL_AWAIT}?since=${encodeURIComponent(
      since
    )}&timeout_ms=${AWAIT_TIMEOUT_MS}`;
    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal,
    });
    if (!res.ok) return null;
    const json = await res.json();
    const versao =
      typeof json?.versao === "number" ? (json.versao as number) : since;
    const changed = Boolean(json?.changed);
    return { changed, versao };
  } catch {
    return null;
  }
}

/** ---------------------- Hook ---------------------- */
export function useVendasSuprimentos(query?: VendasQuery) {
  const [rows, setRows] = useState<SaidaRowUI[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<RespAPI["meta"] | null>(null);

  const stop = useRef(false);
  const fetching = useRef(false);

  const versaoRef = useRef<number | null>(null);
  const awaitCtrlRef = useRef<AbortController | null>(null);
  const awaitSupported = useRef<boolean | null>(null);
  const awaitInFlightRef = useRef(false);

  async function carregar() {
    fetching.current = true;
    try {
      const r = await fetchVendasOnce(query);
      setRows(r.rows);
      setMeta(r.meta);
    } finally {
      fetching.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;
    versaoRef.current = null;
    awaitSupported.current = null;
    awaitCtrlRef.current?.abort();

    (async () => {
      try {
        // primeira carga
        await carregar();
        setLoading(false);

        // detectar suporte ao await
        const init = await awaitChange(-1);
        if (init && typeof init.versao === "number") {
          versaoRef.current = init.versao;
          awaitSupported.current = true;
        } else {
          awaitSupported.current = false;
        }

        // loop principal
        while (!stop.current) {
          while (fetching.current && !stop.current) await sleep(25);
          if (stop.current) break;

          const cur = versaoRef.current;

          // modo await (long-poll)
          if (awaitSupported.current !== false && cur != null) {
            if (awaitInFlightRef.current) {
              await sleep(50);
              continue;
            }

            const ac = new AbortController();
            awaitCtrlRef.current = ac;
            awaitInFlightRef.current = true;

            let resp: AwaitResult | null = null;
            try {
              resp = await awaitChange(cur, ac.signal);
            } catch (e: any) {
              if (e?.name !== "AbortError") {
                await sleep(jitter(ERROR_BACKOFF_MS));
              }
            } finally {
              if (awaitCtrlRef.current === ac) awaitCtrlRef.current = null;
              awaitInFlightRef.current = false;
            }

            if (stop.current) break;

            if (resp === null) {
              if (awaitSupported.current === null)
                awaitSupported.current = false;
              await sleep(POLL_FALLBACK_MS);
              continue;
            } else if (awaitSupported.current === null) {
              awaitSupported.current = true;
            }

            const avancou = (resp.versao ?? -1) > (cur ?? -1);
            if (resp.changed || avancou) {
              versaoRef.current = resp.versao ?? cur;
              await carregar();
              await sleep(20);
            } else {
              await sleep(AWAIT_GAP_MS);
            }
            continue;
          }

          // fallback: polling simples
          await sleep(POLL_FALLBACK_MS);
          await carregar();
        }
      } catch (e) {
        if (!stop.current) console.error("useVendasSuprimentos loop error:", e);
        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
      awaitCtrlRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(query)]);

  const refresh = async () => {
    while (fetching.current) await sleep(25);
    await carregar();
  };

  return { rows, loading, refresh, meta };
}
